<template>
  <RiskTypeTabs
    :all-tabs="allTabs"
    :selected-tab-id="currentTab"
    @selected-tab="filterData($event)"
  />
  <StandardCardContainer>
    <p v-if="!hasData" class="text-center">
      There are no flagged risks in this category.
    </p>
    <DetailedReasonTable
      v-else
      :data="visibleData"
      :show-risk-type-column="currentTab == 'all'"
      @go-to-unit-profile="goToUnitProfile"
    />
  </StandardCardContainer>
  <div v-if="data.length > 20">
    <p class="scroll-message text-center mt-16">
      You've reached the bottom of the list
    </p>
  </div>
</template>

<script setup lang="ts">
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";

import type { RiskStatsDto } from "@/models/dto/riskStatsDto";
import { ref, computed } from "vue";
import RiskTypeTabs from "./RiskTypeTabs.vue";
import { getIndicatorTypesForRiskCategory } from "@/models/indicatorTypesForRiskCategory";
import {
  IndicatorType,
  indicatorTypeToReasonText,
} from "@/models/enum/indicatorType";
import { useStatsStore } from "@/stores/statsStore";
import DetailedReasonTable from "@/views/dashboard-view/DetailedReasonTable.vue";
import { getRiskCategoryColor } from "@/models/enum/riskCategory";

const props = defineProps<{
  data: RiskStatsDto[];
}>();

const emit = defineEmits<{
  (e: "goToUnitProfile", risk: RiskStatsDto): void;
}>();

const visibleData = ref(props.data);
const statsStore = useStatsStore();
const currentTab = ref("all");
const riskColor = computed(
  () => `var(${getRiskCategoryColor(statsStore.selectedRiskCategory)}`
);

const hasData = computed(() => {
  var count = allTabs.value.find((t) => t.id == currentTab.value)?.count;
  return count === undefined ? 0 : count;
});

const allTabs = computed(() => [
  { id: "all", label: "All", count: countRiskType("all") },
  ...getIndicatorTypesForRiskCategory(statsStore.selectedRiskCategory).map(
    (k) => ({
      id: k,
      label: indicatorTypeToReasonText(k as IndicatorType),
      count: countRiskType(k),
    })
  ),
]);

function countRiskType(tab: string) {
  var counter = 0;

  props.data.forEach((s) => {
    s.reasons.forEach((r) => {
      if (tab == "all" || r.indicatorType == tab) {
        counter += r.detailedReasons.length;
      }
    });
  });

  return counter;
}

function filterData(tabId: string) {
  currentTab.value = tabId;
  if (currentTab.value == "all") {
    visibleData.value = props.data;
  } else {
    visibleData.value = props.data.map(
      (r) =>
        ({
          trustGroupId: r.trustGroupId,
          companyName: r.companyName,
          tierLevel: r.tierLevel,
          countryOfOperation: r.countryOfOperation,
          industry: r.industry,
          reasons: r.reasons.filter((x) => x.indicatorType == currentTab.value),
          supplyChain: r.supplyChain,
          organisationNumber: r.organisationNumber,
          riskCategory: r.riskCategory,
        } as RiskStatsDto)
    );
  }
}

function goToUnitProfile(risk: RiskStatsDto) {
  emit("goToUnitProfile", risk);
}
</script>

<style scoped lang="scss">
.scroll-message {
  color: v-bind(riskColor);
}
</style>
